import React, { useState } from 'react';
import { X, Bookmark, Plus } from 'lucide-react';
import { EditorialProject } from '../../../../../types/EditorialProject.types';

const cn = (...args: any[]) => args.filter(Boolean).join(' ');

interface NewProposalModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreateProject: (project: EditorialProject) => void;
}

export default function NewProposalModal({
  isOpen,
  onClose,
  onCreateProject
}: NewProposalModalProps) {
  const [modality, setModality] = useState<'propia' | 'encargo'>('propia');
  const [bookTitle, setBookTitle] = useState('');
  const [bookAuthor, setBookAuthor] = useState('');
  const [clientName, setClientName] = useState('');
  const [bookCategory] = useState('Ensayo y Pensamiento');
  const [collection, setCollection] = useState('Fondo General Albatross');
  const [agentOrAgency, setAgentOrAgency] = useState('');
  const [isSolicited] = useState(true);
  const [targetAudience] = useState('Público general y universitario');
  const [synopsis, setSynopsis] = useState('');
  const [globalBudget, setGlobalBudget] = useState(12000);
  const [targetReleaseDate, setTargetReleaseDate] = useState('2026-11-30');
  const [assignedReader, setAssignedReader] = useState('Pedro Sánchez (Comité de Lectura)');

  if (!isOpen) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!bookTitle || !bookAuthor) return;

    const newProject: EditorialProject = {
      id: `ED-${Date.now().toString().slice(-4)}`,
      name: `Proyecto Editorial: ${bookTitle}`,
      responsableId: 'u-1',
      responsableName: 'Pedro Sánchez',
      bookTitle,
      bookAuthor,
      bookCategory,
      editorialPhase: 'evaluacion',
      modality,
      clientName: modality === 'encargo' ? clientName : undefined,
      status: 'planning',
      globalBudget,
      startDate: new Date().toISOString().split('T')[0],
      targetReleaseDate,
      formats: [
        { name: 'Rústica con Solapas', price: 22.00 },
        { name: 'E-book (ePub)', price: 9.99 }
      ],
      tiradas: [{ id: `T-${Date.now()}`, editionName: '1ª Edición', quantity: 2000, date: targetReleaseDate }],
      humanResources: [],
      physicalResources: [],
      expenses: [],
      proposalDetails: {
        isSolicited,
        genre: bookCategory,
        collection,
        synopsis,
        targetAudience,
        proposalDate: new Date().toISOString().split('T')[0],
        agentOrAgency: agentOrAgency || 'Contacto Directo con Autor',
        crmSyncEnabled: true,
        presaleEnabled: false
      },
      zeroReader: {
        assignedReader,
        deadline: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        report: undefined // vacío a propósito: bloquea el paso a Mesa Editorial hasta que se complete
      },
      editorialBoardDecision: { decision: 'pendiente' },
      contractDetails: {
        status: 'draft',
        advanceAmount: 2500,
        advanceAmortized: 0,
        advanceTreasuryPaid: false,
        royaltyTiers: [{ upToQty: 2000, rate: 8 }, { upToQty: 5000, rate: 10 }, { upToQty: 999999, rate: 12 }],
        territory: 'España y Latinoamérica',
        authorizedLanguages: ['Castellano'],
        authorizedFormats: ['Tapa Dura', 'Rústica', 'E-book'],
        durationYears: 10,
        exclusivity: true
      },
      technicalSheet: {
        isbnPrint: '978-84-15982-20-3',
        isbnDigital: '978-84-15982-21-0',
        legalDeposit: 'M-18490-2026',
        formatSize: '17 x 24 cm',
        pageCount: 350,
        paperType: 'Coral Book Ahuesado 80g',
        bindingType: 'Rústica con solapas cosida con hilo',
        coverLamination: 'Plastificado mate con reservas UVI'
      },
      costEscandallo: {
        services: [
          { id: 'srv-1', concept: 'Corrección de Estilo', budgeted: 1200, actual: 0, provider: 'Correcciones Literarias S.L.', purchaseOrderGenerated: false },
          { id: 'srv-2', concept: 'Maquetación y Diseño Interior', budgeted: 950, actual: 0, provider: 'Estudio Gráfico Tipos', purchaseOrderGenerated: false },
          { id: 'srv-3', concept: 'Diseño de Cubierta', budgeted: 600, actual: 0, provider: 'Ilustraciones Creativas', purchaseOrderGenerated: false }
        ],
        industrials: [
          { id: 'ind-1', concept: 'Papel Interior Ahuesado 80g', budgeted: 2400, actual: 0, provider: 'Torraspapel S.A.', purchaseOrderGenerated: false },
          { id: 'ind-2', concept: 'Impresión Offset y Encuadernación', budgeted: 4200, actual: 0, provider: 'Marcial Pons Gráficas', purchaseOrderGenerated: false }
        ]
      },
      ganttSchedule: [
        { id: 'g-1', taskName: 'Recepción y Lectura Cero', startDate: '2026-09-01', endDate: '2026-09-30', assignee: assignedReader, progressPct: 20, dependencies: [] },
        { id: 'g-2', taskName: 'Deliberación en Mesa Editorial', startDate: '2026-10-01', endDate: '2026-10-10', assignee: 'Comité Editorial', progressPct: 0, dependencies: ['g-1'] },
        { id: 'g-3', taskName: 'Firma Contrato DocuSign', startDate: '2026-10-11', endDate: '2026-10-25', assignee: 'Dpto. Legal', progressPct: 0, dependencies: ['g-2'] },
        { id: 'g-4', taskName: 'Corrección y Maqueta V1', startDate: '2026-10-26', endDate: '2026-11-15', assignee: 'Equipo de Corrección', progressPct: 0, dependencies: ['g-3'] },
        { id: 'g-5', taskName: 'Imprimátur (OK a Pruebas)', startDate: '2026-11-16', endDate: '2026-11-20', assignee: 'Dirección Editorial', progressPct: 0, isImprimatur: true, dependencies: ['g-4'] },
        { id: 'g-6', taskName: 'Impresión y Recepción en Almacén', startDate: '2026-11-21', endDate: '2026-11-30', assignee: 'Marcial Pons', progressPct: 0, dependencies: ['g-5'] }
      ],
      imprimatur: { approved: false, versionNotes: 'Pendiente de inicio de maquetación' },
      printJob: {
        otNumber: `OT-2026-${Math.floor(100 + Math.random() * 900)}`,
        printShop: 'Marcial Pons Gráficas Industriales S.A.',
        contractedRun: 2000,
        effectiveDelivered: 2000,
        tolerancePercent: 5,
        status: 'pendiente'
      },
      authorLiquidation: {
        period: 'Ejercicio Corriente',
        copiesSold: 0,
        netRetailSales: 0,
        grossRoyalties: 0,
        advanceToAmortize: 0,
        netPayable: 0,
        isLiquidated: false,
        financeInvoiceGenerated: false
      }
    };

    onCreateProject(newProject);
    onClose();
  };

  return (
    <div className="ed-modal-overlay">
      <div className="ed-modal">
        <div className="ed-modal-header">
          <div className="ed-modal-title"><Bookmark size={18} style={{ color: 'var(--color-brand-green)' }} /> Alta_De_Nueva_Propuesta_O_Proyecto_Editorial</div>
          <button onClick={onClose} className="ed-modal-close"><X size={18} /></button>
        </div>

        <form onSubmit={handleSubmit} className="ed-modal-form">
          <div className="ed-box">
            <span className="ed-label" style={{ marginBottom: '0.375rem', fontWeight: 700 }}>Modalidad de Explotación Editorial</span>
            <div className="ed-modality-grid">
              <button
                type="button"
                onClick={() => setModality('propia')}
                className={cn('ed-modality-option', modality === 'propia' && 'ed-modality-option--propia-active')}
              >
                <strong>Inversión Propia</strong>
                <span>Fondo editorial propio. La empresa asume el coste de edición, impresión y el riesgo comercial.</span>
              </button>
              <button
                type="button"
                onClick={() => setModality('encargo')}
                className={cn('ed-modality-option', modality === 'encargo' && 'ed-modality-option--encargo-active')}
              >
                <strong>Por Encargo / Cliente</strong>
                <span>Servicios editoriales prestados a un tercero con plan de cobro por hitos y facturación vinculada.</span>
              </button>
            </div>
          </div>

          {modality === 'encargo' && (
            <div className="ed-box" style={{ background: 'rgba(88,28,135,0.15)', borderColor: 'rgba(168,85,247,0.3)' }}>
              <label className="ed-field-label" style={{ color: '#d8b4fe', fontWeight: 700 }}>Nombre de la Empresa o Institución Cliente</label>
              <input type="text" required value={clientName} onChange={(e) => setClientName(e.target.value)} placeholder="Ej. Fundación BBVA / Ilustre Colegio de Abogados" className="ed-input" style={{ marginTop: '0.25rem' }} />
            </div>
          )}

          <div className="ed-grid-2">
            <div className="ed-field">
              <label className="ed-field-label">Título Provisional de la Obra</label>
              <input type="text" required value={bookTitle} onChange={(e) => setBookTitle(e.target.value)} placeholder="Ej. Tratado de Derecho Digital" className="ed-input" style={{ fontWeight: 700 }} />
            </div>
            <div className="ed-field">
              <label className="ed-field-label">Autor / Coautores</label>
              <input type="text" required value={bookAuthor} onChange={(e) => setBookAuthor(e.target.value)} placeholder="Ej. Dra. Elena Garrido" className="ed-input" style={{ fontWeight: 700 }} />
            </div>
          </div>

          <div className="ed-grid-2">
            <div className="ed-field">
              <label className="ed-field-label">Colección Editorial</label>
              <input type="text" value={collection} onChange={(e) => setCollection(e.target.value)} className="ed-input" />
            </div>
            <div className="ed-field">
              <label className="ed-field-label">Agente / Representante / Agencia</label>
              <input type="text" value={agentOrAgency} onChange={(e) => setAgentOrAgency(e.target.value)} placeholder="Ej. Agencia Literaria Carmen Balcells" className="ed-input" />
            </div>
          </div>

          <div className="ed-field">
            <label className="ed-field-label">Sinopsis Ejecutiva</label>
            <textarea rows={3} value={synopsis} onChange={(e) => setSynopsis(e.target.value)} placeholder="Descripción temática del manuscrito y justificación editorial..." className="ed-textarea" />
          </div>

          <div className="ed-grid-2">
            <div className="ed-field">
              <label className="ed-field-label">Lector Cero Asignado (Evaluador)</label>
              <input type="text" value={assignedReader} onChange={(e) => setAssignedReader(e.target.value)} className="ed-input" />
            </div>
            <div className="ed-field">
              <label className="ed-field-label">Presupuesto Global Estimado (€)</label>
              <input type="number" step={500} value={globalBudget} onChange={(e) => setGlobalBudget(Number(e.target.value))} className="ed-input" style={{ fontWeight: 700 }} />
            </div>
          </div>

          <div className="ed-field">
            <label className="ed-field-label">Lanzamiento Previsto</label>
            <input type="date" value={targetReleaseDate} onChange={(e) => setTargetReleaseDate(e.target.value)} className="ed-input" />
          </div>

          <div className="ed-modal-footer">
            <button type="button" onClick={onClose} className="ed-btn ed-btn--outline">Cancelar</button>
            <button type="submit" className="ed-btn ed-btn--primary"><Plus size={14} /> Registrar Propuesta</button>
          </div>
        </form>
      </div>
    </div>
  );
}
